import { Pagination, Card, Divider, Button, message } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getTransactions } from '../../methods/redux/reducers/transactions';
import baseuri from '../../methods/baseuri';
// import { commas } from '../../methods/utils/general-utils';
import SearchFilter from '../../component/Filter';
import { getCurrencies } from '../../methods/redux/reducers/currency';
import TransactionItem from './TransactionItem';
import { getUserProfileById } from '../../methods/redux/reducers/user-profile';
import NativeNumber from '../../component/Numeral';
import remakeQueryString from '../../methods/utils/remake-query-string';
import {
	commas,
	transactionStatementTypes,
} from '../../methods/utils/general-utils';
import { getGlobalEnumsData } from '../../methods/redux/reducers/general';

const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const TransactionStatments = () => {
	const dispatch = useAppDispatch();
	const { userId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const getQueryParams = () => Object.fromEntries(searchParams.entries());

	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);

	const { globalEnums } = useAppSelector((store) => store.general);
	const { currencies } = useAppSelector((store) => store.currencies);
	const { user } = useAppSelector((store) => store.userProfile);
	const { itemsList, paginator } = useAppSelector(
		(store) => store.transactions
	);

	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				status: 'active',
				pageId: '1',
				perPage: String(paginator.perPage),
			});
		}

		if (userId) {
			dispatch(getUserProfileById(userId));
		}

		dispatch(getGlobalEnumsData());
		dispatch(getCurrencies({}));
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			if (userId) {
				dispatch(getTransactions({ ...queryData, userId }));
			} else {
				dispatch(getTransactions(queryData));
			}
		}
	}, [searchParams]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item._id,
	}));

	// Transform the results data to match the headers
	const transformDownloadData = (requests: any[]) =>
		requests?.map((data: any) => ({
			transactionType: data?.transactionType,
			transactionAmount: `${data?.transactionAmount} ${data?.currencyId?.symbol}`,
			username: data?.userId?.username,
			status: data.status,
			transactionForm: data.transactionForm,
			blockchainNetwork: `${
				data?.blockchainTransactionNetworkId?.abbreviation
					? data?.blockchainTransactionNetworkId?.abbreviation
					: '--'
			}`,
			blockchainTransactionHash: `${
				data.blockchainTransactionHash
					? `https://${data.blockchainTransactionNetworkId?.explorerUrl}/tx/${data.blockchainTransactionHash}`
					: '--'
			}`,
			actualAmount: `${commas(data?.totalDeposit || 0)} ${
				data?.currencyId?.symbol
			}`,
			associatedTransactionFee: data?.associatedTransactionFee,
			description: data?.description,
			oldWalletBalance: `${commas(data?.oldWalletBalance || 0)} ${
				data?.currencyId?.symbol
			}`,
			newWalletBalance: `${commas(data?.newWalletBalance || 0)} ${
				data?.currencyId?.symbol
			}`,
			usdActualAmountValue: data?.usdActualAmountValue,
			createdAt: moment(data.createdAt).format('lll'), // Handle formatted date
			updatedAt: moment(data.createdAt).format('lll'), // Handle formatted date
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'Transaction Type', key: 'transactionType' },
		{ label: 'Transaction Amount', key: 'transactionAmount' },
		{ label: 'Username', key: 'username' },
		{ label: 'Status', key: 'status' },
		{ label: 'Transaction Form', key: 'transactionForm' },
		{ label: 'Blockchain Network', key: 'blockchainNetwork' },
		{ label: 'Transaction Hash', key: 'blockchainTransactionHash' },
		{ label: 'Actual Amount', key: 'actualAmount' },
		{ label: 'Transaction Fee', key: 'associatedTransactionFee' },
		{ label: 'Description', key: 'description' },
		{ label: 'Old Wallet Balance', key: 'oldWalletBalance' },
		{ label: 'New Wallet Balance', key: 'newWalletBalance' },
		{ label: 'Usd Actual Amount', key: 'usdActualAmountValue' },
		{ label: 'Created', key: 'createdAt' },
		{ label: 'Last Updated', key: 'updatedAt' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();

		setResults([]);
		baseuri
			.get('/admin/statements/transactions' || '', {
				params: {
					...params1,
					perPage: String(paginator.perPage) || 0,
					// perPage: paginator?.itemCount || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res.data.data.itemsList;
				const transformedData = transformDownloadData(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('Could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<div>
			<Card>
				<h1>
					Transaction Statements{' '}
					{userId && (
						<span>
							{' '}
							for{' '}
							<Link
								title="View Profile"
								to={`/user-profile/${user.profile?._id}`}
							>
								@{user.profile?.username}
							</Link>
						</span>
					)}
				</h1>
				<p>Find all wallet credit and debit records on Xend Finance.</p>

				<Divider />

				<SearchFilter
					title="Search Transaction Statements"
					searchFilters={[
						{
							label: 'Status',
							name: 'status',
							defaultSelectedValue: 'active',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Pending', value: 'pending' },
								{ name: 'Active', value: 'active' },
							],
						},
						{
							label: 'Type',
							name: 'transactionType',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Credit', value: 'credit' },
								{ name: 'Debit', value: 'debit' },
								{ name: 'Unknown', value: 'unknown' },
							],
						},
						{
							label: 'Currency',
							name: 'currencyId',
							htmlElement: 'select',
							selectOptions: currencySelect,
						},
						{
							label: 'Transaction Form',
							name: 'transactionForm',
							htmlElement: 'select',
							selectOptions: transactionStatementTypes(
								globalEnums.TRANSACTION_STATEMENT_FORM
							),
						},
						{
							label: 'Start Date',
							name: 'startDate',
							htmlElement: 'datepicker',
						},
						{
							label: 'End Date',
							name: 'endDate',
							htmlElement: 'datepicker',
						},
					]}
					retrieveQueryString={updateQueryString}
				/>

				<br />

				<p>
					Total of{' '}
					<strong>
						<NativeNumber value={paginator.itemCount} /> items
					</strong>{' '}
					found
				</p>

				<Divider />

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					total={paginator.itemCount}
					current={paginator.currentPage}
					pageSize={paginator.perPage}
				/>

				<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<DownloadButton
						danger
						loading={loading}
						icon={<DownloadOutlined />}
						onClick={() => handleDownload()}
					>
						{loading ? 'Loading csv...' : 'Download CSV'}
					</DownloadButton>
					<CSVLink
						data={results}
						headers={headers}
						className="download"
						filename="Transaction Statement.csv"
						ref={csvLinkRef}
					/>
				</div>

				<br />

				{itemsList?.map((item: any) => (
					<TransactionItem key={item._id} transaction={item} />
				))}

				<Divider />

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					total={paginator.itemCount}
					current={paginator.currentPage}
					pageSize={paginator.perPage}
					showSizeChanger
					showQuickJumper
				/>
			</Card>
		</div>
	);
};

export default TransactionStatments;
